import { groupBy } from 'src/utils'
import { resetarParaEstadoInicial } from 'src/utils/executoras/executoras'
import { formatarDataParaEnvio } from 'src/utils/formatadoras/formatadoras'
import {
  vuexMapDefaultsMutations,
  vuexMapDefaultsActions,
  vuexMapDefaultsGetters
} from 'src/utils.store'

import {
  StatusSolicitacao,
  TipoDeEntidade
} from 'src/components/Approvals/utils'

import { API } from 'src/services'
import config from 'src/config'
import { legacyHttp } from 'src/legacy/http'
import moment from 'moment'
import { normalizarFuncionariosPorStatus } from './refinamentoVisual'
import {
  buscarDadosDeFerias,
  filtrarEmpregadosPorLiderDireto,
  montarEntidadesDoAgrupamento,
  obterRepositorioDeFerias
} from '@/components/Home/Components/widgets/feriasDoTime'

import { FiltradorDeErros } from 'src/erros'

import {
  converterChavesDeObjeto,
  chavesReferentesAosEstadoTrabalhando
} from 'src/components/AcompanhamentoTempoReal/utils'
import { obterFonteDeDadosAcompanhamentoEmTempoReal } from 'src/components/AcompanhamentoTempoReal/fontesDeDados'

// Mapeia o tipo de agrupamento da Tela Inicial para o queryKind aceito pelo
// endpoint legado de aprovações (getallapprovals).
const MAPA_QUERY_KIND_APROVACAO = {
  localDeTrabalho: 'company',
  grupo: 'userGroup',
  liderDireto: 'employee'
}

// Mapeia o tipo de agrupamento para o TipoDeEntidade aceito pelo endpoint novo
// de solicitações de aprovação (solicitacao-de-aprovacao).
const MAPA_TIPO_ENTIDADE_APROVACAO = {
  localDeTrabalho: TipoDeEntidade.localDeTrabalho,
  grupo: TipoDeEntidade.grupo,
  liderDireto: TipoDeEntidade.empregado
}

const CAMPOS_EMPREGADOS_DO_ESCOPO = [
  'id',
  'code',
  'name',
  'company',
  'resignation'
]
const TAMANHO_LOTE_LIDERANCA_DIRETA = 100

async function buscarEmpregadosComLiderancaDireta (empregados) {
  const lotes = []
  for (let inicio = 0; inicio < empregados.length; inicio += TAMANHO_LOTE_LIDERANCA_DIRETA) {
    lotes.push(empregados.slice(inicio, inicio + TAMANHO_LOTE_LIDERANCA_DIRETA))
  }

  const respostas = await Promise.all(lotes.map(async lote => {
    const parametros = new URLSearchParams({
      active: 'false',
      fired: 'all',
      noderef: 'bulk',
      fields: JSON.stringify([...CAMPOS_EMPREGADOS_DO_ESCOPO, 'usuario_lider_direto_id'])
    })
    lote.forEach(empregado => parametros.append('multiple_ids', empregado.id))

    const resposta = await legacyHttp.get(`${config.baseUrl}/api/employee?${parametros.toString()}`)
    return resposta.body?.success || []
  }))

  return respostas.flat()
}

// Constrói a chave de filtro (companies/userGroups/employees) aceita no corpo
// dos endpoints que recebem lista de entidades.
function montarFiltroDeEntidades (parametros) {
  if (parametros.tipo === 'grupo') {
    return { userGroups: parametros.userGroups || [] }
  }
  if (parametros.tipo === 'liderDireto') {
    return { employees: parametros.employees || [] }
  }
  return { companies: parametros.companies || [] }
}

// Lista de ids do agrupamento atual usada nos endpoints de aprovação.
function obterIdsDoAgrupamento (parametros) {
  if (parametros.tipo === 'grupo') return parametros.userGroups || []
  if (parametros.tipo === 'liderDireto') return parametros.employees || []
  return parametros.companies || []
}

// Compõe o filtro do endpoint acompanhamento-diario/geral (widget "Atraso da
// equipe"). Diferente das outras estatísticas, este endpoint aceita
// grupos/locais/líderes nativamente, então funciona nos 3 agrupamentos.
// É sempre "hoje" (dia = data atual) — ignora a competência/seletor de mês.
function comporFiltroAtraso (parametros) {
  const filtro = { dia: moment().format('YYYY-MM-DD') }
  if (parametros.tipo === 'grupo') {
    filtro.grupos = parametros.userGroups || []
  } else if (parametros.tipo === 'liderDireto') {
    // Só manda params de liderança quando a empresa tem o módulo de hierarquia
    // ligado (espelha AcompanhamentoTempoReal/state.js); senão, cai para a
    // lista de empregados do escopo.
    if (parametros.hasAcessoPorLideranca && parametros.usuarioLider) {
      filtro.usuariosLideres = [parametros.usuarioLider]
      filtro.modalidadeDeAcesso = 'lideranca_direta'
    } else {
      filtro.empregadores = parametros.employees || []
    }
  } else {
    filtro.locaisDeTrabalho = parametros.companies || []
  }
  return filtro
}

// Banco de horas da equipe (whb-window-management): buscamos todos os empregados
// do agrupamento numa única página, pois o widget mostra o Top 6 mas precisa do
// total de empregados e da soma dos saldos de todos.
const LIMITE_EMPREGADOS_BANCO_EQUIPE = 9999

function defaultState () {
  return {
    carregando: false,
    type: 'month',
    startDate: null,
    endDate: null,
    // Modo do widget "Banco de horas da equipe":
    // 'competencia' = saldo do mês selecionado (startDate→endDate)
    // 'total'       = saldo acumulado até agora (endDate omitido)
    modoBancoDeHoras: 'competencia',
    mesReferencia: null,
    company: {
      loaded: [],
      selected: null
    },
    // Agrupamento de colaboradores da Tela Inicial.
    // tipo: 'localDeTrabalho' | 'grupo' | 'liderDireto'
    tipoAgrupamento: 'localDeTrabalho',
    grupoSelecionado: {
      loaded: [],
      selected: null
    },
    liderSelecionado: {
      loaded: [],
      selected: null
    },
    // Cache (por id do usuário logado) dos empregados do escopo de acesso,
    // usado como base para os agrupamentos da Home.
    empregadosDoEscopoCache: {},
    // Mesmo escopo enriquecido com usuarioLiderDireto. O endpoint legado só
    // materializa essa relação quando recebe os ids explicitamente.
    empregadosComLiderancaDoEscopoCache: {},
    // Ids dos locais que têm ao menos 1 funcionário ATIVO (derivado do escopo).
    // totalOfEmployees inclui demitidos, então não serve para filtrar o seletor
    // de local — este conjunto sim reflete "locais com quem acompanhar".
    idsLocaisComEmpregadoAtivo: [],
    // Último filtro de agrupamento aplicado, reaproveitado pela paginação do
    // banco de horas antigo.
    filtroDeAgrupamentoAtual: null,
    estatisticaJornadasComProblemas: {
      quantidade: 0,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    },
    estatisticaPedidosDeAprovacao: {
      quantidade: 0,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    },
    estatisticaTravamentosPendentes: {
      quantidade: 0,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    },
    estatisticaTravamentoEquipe: {
      notStarted: 0,
      empPending: 0,
      managerPending: 0,
      finished: 0,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    },
    registroBancoDeHorasEmpregado: {
      acumuladoPositivo: [],
      acumuladoNegativo: [],
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false,
      page: 1,
      totalDePaginas: 1
    },
    eventosExcepcionais: {
      eventos: {},
      indisponivelPorAgrupamento: false,
      loading: false,
      error: false
    },
    eventosProgramados: {
      eventos: {},
      itens: [],
      vacationPerDay: {},
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    },
    // Widget "Atraso da equipe" (Gestor) — foto do dia (tempo real), gated por
    // controle_custos. Reaproveita o endpoint acompanhamento-diario/geral.
    atrasoEquipe: {
      loading: true,
      error: false,
      // Reservado; hoje sempre false (o endpoint aceita grupo/líder nativamente).
      indisponivelPorAgrupamento: false,
      total: 0,
      contagens: { emAtraso: 0, aIniciar: 0, trabalhando: 0, ausentes: 0 },
      funcionariosPorStatus: { emAtraso: [], aIniciar: [], trabalhando: [], ausentes: [] },
      horarioUltimaAtualizacao: null
    },
    // Widget "Férias do time" (Gestor) — próximas férias (marcadas/pedidas) e
    // saldos a vencer nos próximos 180 dias. Gated por acompanhamento_ferias.
    feriasDoTime: {
      loading: true,
      error: false,
      proximas: [],
      vencimentos: [],
      totalProximas: 0,
      totalVencimentos: 0
    }
  }
}

const getters = {
  ...vuexMapDefaultsGetters(defaultState())
}

const mutations = {
  ...vuexMapDefaultsMutations(defaultState()),
  resetState (state) {
    resetarParaEstadoInicial(state, defaultState())
  },
  limparAcumulados (state) {
    state.registroBancoDeHorasEmpregado.acumuladoPositivo = []
    state.registroBancoDeHorasEmpregado.acumuladoNegativo = []
    state.registroBancoDeHorasEmpregado.page = 1
    state.registroBancoDeHorasEmpregado.totalDePaginas = 1
  }
}

const actions = {
  ...vuexMapDefaultsActions(defaultState()),
  // Resolve (e cacheia por usuário logado) a lista de empregados do escopo de
  // acesso. Quando recebe um líder, enriquece o escopo e recorta os liderados.
  async resolverEmpregadosDoEscopo ({ state, commit, rootState }, idLiderDireto) {
    const idUsuarioLogado = rootState.userInfo.id

    try {
      let empregados = state.empregadosDoEscopoCache[idUsuarioLogado]
      if (!empregados) {
        const resposta = await API.employee.get({
          active: 'false',
          order_by: 'name',
          fired: 'all',
          noderef: 'bulk',
          fields: JSON.stringify(CAMPOS_EMPREGADOS_DO_ESCOPO)
        })
        empregados = (resposta.body.success || []).filter(empregado => !empregado.resignation)
        commit('empregadosDoEscopoCache', {
          ...state.empregadosDoEscopoCache,
          [idUsuarioLogado]: empregados
        })
      }

      if (!idLiderDireto) return empregados

      let empregadosComLideranca = state.empregadosComLiderancaDoEscopoCache[idUsuarioLogado]
      if (!empregadosComLideranca) {
        empregadosComLideranca = await buscarEmpregadosComLiderancaDireta(empregados)
        commit('empregadosComLiderancaDoEscopoCache', {
          ...state.empregadosComLiderancaDoEscopoCache,
          [idUsuarioLogado]: empregadosComLideranca
        })
      }

      return filtrarEmpregadosPorLiderDireto(empregadosComLideranca, idLiderDireto)
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      return []
    }
  },
  // Deriva (do escopo já carregado) os ids dos locais que têm ao menos 1
  // funcionário ATIVO. Usado para filtrar o seletor de local da Tela Inicial:
  // totalOfEmployees inclui demitidos e deixava passar locais que abrem vazios.
  async resolverLocaisComEmpregadoAtivo ({ dispatch, commit }) {
    const empregados = await dispatch('resolverEmpregadosDoEscopo')
    const ids = [...new Set(
      empregados.flatMap(empregado => (empregado.company || []).map(local => local.id)).filter(Boolean)
    )]
    commit('idsLocaisComEmpregadoAtivo', ids)
    return ids
  },
  async carregarDadosTravamentosPendentes ({commit, state}, parametros) {
    // locker-statistics só aceita filtro por company. Em Grupo/Líder direto a
    // estatística viria inflada (somaria todo o local), então marcamos como
    // indisponível para o agrupamento e não disparamos a request.
    if (parametros.tipo === 'grupo' || parametros.tipo === 'liderDireto') {
      commit('estatisticaTravamentosPendentes', {
        quantidade: state.estatisticaTravamentosPendentes.quantidade,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      commit('estatisticaTravamentoEquipe', {
        ...state.estatisticaTravamentoEquipe,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      return
    }

    commit('estatisticaTravamentosPendentes', {
      quantidade: state.estatisticaTravamentosPendentes.quantidade,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })
    commit('estatisticaTravamentoEquipe', {
      ...state.estatisticaTravamentoEquipe,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })

    try {
      const startDate = moment(state.startDate).format('YYYY-MM-DD')
      const endDate = moment(state.endDate).format('YYYY-MM-DD')
      const companies = parametros.companies || []
      // locker-statistics filtra por um único local. Agregamos os resultados de
      // cada local do agrupamento e estreitamos pelo líder quando aplicável.
      const respostas = await Promise.allSettled(
        companies.map(idCompany => API.estatisticaDeBloqueio.get({
          startDate,
          endDate,
          company: idCompany,
          user: parametros.usuarioLider || parametros.user,
          comp_man: parametros.comp_man
        }))
      )

      const bloqueios = respostas
        .filter(resposta => resposta.status === 'fulfilled')
        .map(resposta => resposta.value.body.success || {})
      const somarBucket = chave => bloqueios.reduce((total, bloqueio) => total + (bloqueio[chave]?.length || 0), 0)
      const notStarted = somarBucket('notStarted')

      commit('estatisticaTravamentosPendentes', {
        quantidade: notStarted,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
      // Os 4 buckets do locker-statistics alimentam o widget Travamento (%+estados).
      commit('estatisticaTravamentoEquipe', {
        notStarted,
        empPending: somarBucket('empPending'),
        managerPending: somarBucket('managerPending'),
        finished: somarBucket('finished'),
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('estatisticaTravamentosPendentes', {
        quantidade: state.estatisticaTravamentosPendentes.quantidade,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
      commit('estatisticaTravamentoEquipe', {
        ...state.estatisticaTravamentoEquipe,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }
  },
  async carregarDadosDeAprovacao ({commit, state}, parametros) {
    // Líder direto: não há como resolver os liderados do líder (a contagem viria
    // do escopo inteiro → inflada), então marcamos como indisponível para este
    // agrupamento e não disparamos a request.
    if (parametros.tipo === 'liderDireto') {
      commit('estatisticaPedidosDeAprovacao', {
        quantidade: state.estatisticaPedidosDeAprovacao.quantidade,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      return
    }

    commit('estatisticaPedidosDeAprovacao', {
      quantidade: state.estatisticaPedidosDeAprovacao.quantidade,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })
    try {
      const idsDoAgrupamento = obterIdsDoAgrupamento(parametros)

      const response = await API.approvals.save({
        queryKind: MAPA_QUERY_KIND_APROVACAO[parametros.tipo] || 'company',
        idList: idsDoAgrupamento,
        modelKind: 'all',
        approveKind: 'pending',
        startDate: moment(state.startDate).toISOString(),
        endDate: moment(state.endDate).toISOString()
      })
      const aprovacoesPendentes = response.body.success

      const resposta = await API.solicitacaoDeAprovacao.save({
        tipoDeEvento: null,
        status: StatusSolicitacao.Pendente,
        entidades: {
          tipo: MAPA_TIPO_ENTIDADE_APROVACAO[parametros.tipo] || TipoDeEntidade.localDeTrabalho,
          ids: idsDoAgrupamento
        },
        dataInicial: formatarDataParaEnvio(state.startDate),
        dataFinal: formatarDataParaEnvio(state.endDate)
      })

      const aprovacoesNovoFormatoPendentes = resposta.body

      commit('estatisticaPedidosDeAprovacao', {
        quantidade: aprovacoesPendentes.length + aprovacoesNovoFormatoPendentes.totalDeItens,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('estatisticaPedidosDeAprovacao', {
        quantidade: state.estatisticaPedidosDeAprovacao.quantidade,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }
  },
  async carregarDadosJornadasComProblemas ({commit, state}, parametros) {
    // Líder direto: não há como resolver os liderados do líder (a contagem viria
    // do escopo inteiro → inflada), então marcamos como indisponível para este
    // agrupamento e não disparamos a request.
    if (parametros.tipo === 'liderDireto') {
      commit('estatisticaJornadasComProblemas', {
        quantidade: state.estatisticaJornadasComProblemas.quantidade,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      commit('carregando', false)
      return
    }

    commit('estatisticaJornadasComProblemas', {
      quantidade: state.estatisticaJornadasComProblemas.quantidade,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })

    try {
      const resultadoAPI = await API.timesheetVerify.save({
        ...montarFiltroDeEntidades(parametros),
        onlyTotal: true,
        startDate: moment(state.startDate).format('YYYY-MM-DD'),
        endDate: moment(state.endDate).format('YYYY-MM-DD')
      })

      const { totals } = resultadoAPI.body.success

      const jornadaComProblemas = totals?.oddPts
      commit('estatisticaJornadasComProblemas', {
        quantidade: jornadaComProblemas,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('estatisticaJornadasComProblemas', {
        quantidade: state.estatisticaJornadasComProblemas.quantidade,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }

    commit('carregando', false)
  },
  async carregarEventosProgramados ({commit, state}, parametros) {
    // dayjourney-statistics-absence-expected só aceita filtro por company. Em
    // Grupo/Líder direto o número viria inflado, então marcamos como
    // indisponível para o agrupamento e não disparamos a request.
    if (parametros.tipo === 'grupo' || parametros.tipo === 'liderDireto') {
      commit('eventosProgramados', {
        ...state.eventosProgramados,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      return
    }

    commit('eventosProgramados', {
      ...state.eventosProgramados,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })
    try {
      const startDate = moment().format('YYYY-MM-DD')
      const endDate = moment().add(15, 'days').format('YYYY-MM-DD')
      const companies = parametros.companies || []
      // dayjourney-statistics-absence-expected filtra por um único local.
      // Agregamos os locais do agrupamento e estreitamos pelo líder quando aplicável.
      const respostas = await Promise.allSettled(
        companies.map(idCompany => API.estatisticaDiariaDeAusensiaEsperada.get({
          company: idCompany,
          user: parametros.usuarioLider || parametros.user,
          comp_man: parametros.comp_man,
          startDate,
          endDate
        }))
      )
      const sucessos = respostas
        .filter(resposta => resposta.status === 'fulfilled')
        .map(resposta => resposta.value.body.success || {})
      const expectedAbsence = sucessos.flatMap(sucesso => sucesso.expectedAbsence || [])
      const vacationPerDay = sucessos.reduce((acumulado, sucesso) => ({ ...acumulado, ...(sucesso.vacationPerDay || {}) }), {})

      // Férias
      /*
          Será listado no componente a quantidade de Colaboradores de férias no período.
          Saliento que no painel de acompanhamento, é listado a somatória da quantidade
          de dias de férias dos colaboradores
        */
      const ferias = expectedAbsence.filter(item => item.info?.is_vacation === 'True')
      const feriasAgrupadasPorEmpregado = groupBy(ferias, 'employeeId')

      // Dispensas
      const dispensas = expectedAbsence.filter(item => item.info?.justify?.typeId === 'justify')

      // Suspensões
      const suspensoes = expectedAbsence.filter(item => item.info?.justify?.typeId === 'suspension')

      const eventosProgramados = {
        ferias: feriasAgrupadasPorEmpregado.length,
        dispensas: dispensas.length,
        suspensoes: suspensoes.length
      }

      commit('eventosProgramados', {
        eventos: eventosProgramados,
        itens: expectedAbsence || [],
        vacationPerDay: vacationPerDay || {},
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('eventosProgramados', {
        eventos: state.eventosProgramados,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }
  },
  async carregarEventosExcepcionais ({commit, state}, parametros) {
    // dayjourney-statistics-unexpected só aceita filtro por company. Em
    // Grupo/Líder direto o número viria inflado, então marcamos como
    // indisponível para o agrupamento e não disparamos a request.
    if (parametros.tipo === 'grupo' || parametros.tipo === 'liderDireto') {
      commit('eventosExcepcionais', {
        ...state.eventosExcepcionais,
        indisponivelPorAgrupamento: true,
        loading: false,
        error: false
      })
      return
    }

    commit('eventosExcepcionais', {
      eventos: state.eventosExcepcionais.eventos,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })
    try {
      const startDate = moment(state.startDate).format('YYYY-MM-DD')
      const endDate = moment(state.endDate).format('YYYY-MM-DD')
      const companies = parametros.companies || []
      // dayjourney-statistics-unexpected filtra por um único local.
      // Agregamos os locais do agrupamento e estreitamos pelo líder quando aplicável.
      const respostas = await Promise.allSettled(
        companies.map(idCompany => API.estatisticaDiariaInesperada.get({
          company: idCompany,
          user: parametros.usuarioLider || parametros.user,
          comp_man: parametros.comp_man,
          startDate,
          endDate
        }))
      )
      const sucessos = respostas
        .filter(resposta => resposta.status === 'fulfilled')
        .map(resposta => resposta.value.body.success || {})

      const absence = sucessos.flatMap(sucesso => sucesso.absence || [])
      const holidayWorked = sucessos.flatMap(sucesso => sucesso.holidayWorked || [])
      const dayOffWorked = sucessos.flatMap(sucesso => sucesso.dayOffWorked || [])

      const eventosExpecionais = {
        faltas: absence.length,
        feriadosTrabalhados: holidayWorked.length,
        folgasTrabalhadas: dayOffWorked.length
      }
      commit('eventosExcepcionais', {
        eventos: eventosExpecionais,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: false
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('eventosExcepcionais', {
        eventos: state.eventosExcepcionais.eventos,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }
  },
  async irParaPaginaBancoDeHorasAntigo ({state, commit, dispatch, rootState}, page) {
    const { totalDePaginas } = state.registroBancoDeHorasEmpregado
    if (page < 1 || page > totalDePaginas) return

    commit('registroBancoDeHorasEmpregado', {
      ...state.registroBancoDeHorasEmpregado,
      page
    })

    // Reaproveita o último filtro de agrupamento aplicado. Fallback para o
    // local de trabalho selecionado, mantendo o comportamento legado.
    const filtroAtual = state.filtroDeAgrupamentoAtual || {
      tipo: 'localDeTrabalho',
      companies: state.company.selected ? [state.company.selected.id] : []
    }
    const parametrosDaRequisicao = {
      ...montarFiltroDeEntidades(filtroAtual),
      user: rootState.userInfo.id,
      comp_man: rootState.userInfo.compMan.id,
      showResignated: false
    }
    await dispatch('carregandoDadosBancoDeHorasAntigo', parametrosDaRequisicao)
  },
  async verificarECarregarBancoDeHoras ({dispatch, commit, rootState}, parametros) {
    const possuiModuloBancoDeHoras = rootState.userInfo.compMan.hasWhbank
    if (!possuiModuloBancoDeHoras) {
      return
    }

    // Líder direto: não há API para resolver os liderados do líder; o banco por
    // escopo viria inflado. Marcamos como indisponível para este agrupamento e
    // não disparamos nenhuma request de banco.
    if (parametros.tipo === 'liderDireto') {
      commit('registroBancoDeHorasEmpregado', {
        acumuladoPositivo: [],
        acumuladoNegativo: [],
        loading: false,
        error: false,
        indisponivelPorAgrupamento: true,
        page: 1,
        totalDePaginas: 1
      })
      return
    }

    const ehBancoDeHorasNovo = await dispatch('verificarBancoDeHorasNovoOuAntigo')

    if (ehBancoDeHorasNovo === 'erro') return
    const parametrosDaRequisicao = {
      ...montarFiltroDeEntidades(parametros),
      user: parametros.user,
      comp_man: parametros.comp_man,
      showResignated: false
    }

    if (ehBancoDeHorasNovo) {
      await dispatch('carregarDadosBancosDeHorasNovo', parametrosDaRequisicao)
    } else {
      await dispatch('carregandoDadosBancoDeHorasAntigo', parametrosDaRequisicao)
    }
  },
  async carregarDadosBancosDeHorasNovo ({state, commit}, parametrosDaRequisicao) {
    commit('registroBancoDeHorasEmpregado', {
      acumuladoPositivo: state.registroBancoDeHorasEmpregado.acumuladoPositivo,
      acumuladoNegativo: state.registroBancoDeHorasEmpregado.acumuladoNegativo,
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false
    })
    // Banco por janela da equipe: MESMO endpoint da tela oficial de banco da
    // equipe (Whb/WhbManagement.vue → buscarInfoDeBancoPorJanela →
    // whb-window-management). O endpoint devolve TODOS os ciclos por empregado
    // (currentCicle = ciclo atual/competência; accumulatedTotal = acumulado
    // geral), sempre relativo a hoje — por isso NÃO recebe startDate/endDate (a
    // tela oficial também não envia; o seletor de mês só existe no banco antigo).
    // O "modo" apenas escolhe qual campo do mesmo retorno ler.
    const ehModoTotal = state.modoBancoDeHoras === 'total'
    const params = {
      ...parametrosDaRequisicao,
      page: 1,
      perPage: LIMITE_EMPREGADOS_BANCO_EQUIPE
    }
    try {
      const response = await API.whbWindowManagement.save(params)

      if (response.status === 200) {
        // WhbManagement trata os dois formatos: success como array direto ou
        // como objeto { duePerEmployees: [...] }.
        const sucesso = response.body.success
        const bancoPorEmpregado = Array.isArray(sucesso)
          ? sucesso
          : (sucesso?.duePerEmployees ?? [])
        const acumuladoTotal = bancoPorEmpregado
          .map(banco => ({
            idEmpregado: banco.employee.id,
            empregado: banco.employee.name,
            horas: ehModoTotal ? banco.accumulatedTotal : banco.currentCicle
          }))
          .filter(item => typeof item.horas === 'string' && item.horas.length > 0)

        const acumuladoNegativo = acumuladoTotal.filter(obj => obj.horas.charAt(0) === '-')
        const acumuladoPositivo = acumuladoTotal.filter(obj => obj.horas.charAt(0) !== '-')

        commit('registroBancoDeHorasEmpregado', {
          acumuladoPositivo: acumuladoPositivo,
          acumuladoNegativo: acumuladoNegativo,
          indisponivelPorAgrupamento: false,
          loading: false,
          error: false
        })
      }
    } catch (erro) {
      const erroRegraDeBancoDeHoras = erro.body?.erro === 'Empregado não possui uma regra de banco de horas.'
      const erroRegraDeCompensacaoPorJanela = erro.body?.error === 'Empregado não possui uma regra de compensação por janela.'
      if (erroRegraDeBancoDeHoras || erroRegraDeCompensacaoPorJanela) {
        commit('registroBancoDeHorasEmpregado', {
          acumuladoPositivo: state.registroBancoDeHorasEmpregado.acumuladoPositivo,
          acumuladoNegativo: state.registroBancoDeHorasEmpregado.acumuladoNegativo,
          indisponivelPorAgrupamento: false,
          loading: false,
          error: false
        })
        return
      }
      FiltradorDeErros.capturarErro(erro)
      commit('registroBancoDeHorasEmpregado', {
        acumuladoPositivo: state.registroBancoDeHorasEmpregado.acumuladoPositivo,
        acumuladoNegativo: state.registroBancoDeHorasEmpregado.acumuladoNegativo,
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true
      })
    }
  },
  async carregandoDadosBancoDeHorasAntigo ({state, commit}, parametrosDaRequisicao) {
    commit('registroBancoDeHorasEmpregado', {
      acumuladoPositivo: [],
      acumuladoNegativo: [],
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false,
      page: state.registroBancoDeHorasEmpregado.page,
      totalDePaginas: state.registroBancoDeHorasEmpregado.totalDePaginas
    })
    // Variante legada (whb-manager-multiple-employees): não recebe endDate — o
    // recorte é feito por startDate e o saldo vem em `hours_formatted_monthly`.
    // No modo 'total' (state.modoBancoDeHoras) mantemos a chamada sem endDate
    // (best-effort), mas NÃO há garantia de que ela devolva o acumulado até agora
    // em vez do mês: o TOTAL desta variante precisa de validação com dado real.
    const params = {
      ...parametrosDaRequisicao,
      startDate: moment(state.startDate).format('YYYY-MM-DD'),
      page: Math.max(state.registroBancoDeHorasEmpregado.page - 1, 0),
      perPage: 9999
    }
    try {
      const response = await API.whboManagerNew.save(params)

      if (response.status === 200) {
        const { success: infoEmpregados, totalDePaginas } = response.body
        const bancoDeHorasEmpregados = infoEmpregados.map(empregado => {
          return {
            idEmpregado: empregado.employee.id,
            empregado: empregado.employee.name,
            horas: empregado.hours_formatted_monthly.all_times
          }
        })

        const acumuladoNegativo = bancoDeHorasEmpregados.filter(obj => obj.horas.charAt(0) === '-')
        const acumuladoPositivo = bancoDeHorasEmpregados.filter(obj => obj.horas.charAt(0) !== '-')

        commit('registroBancoDeHorasEmpregado', {
          acumuladoPositivo: acumuladoPositivo,
          acumuladoNegativo: acumuladoNegativo,
          indisponivelPorAgrupamento: false,
          loading: false,
          error: false,
          page: state.registroBancoDeHorasEmpregado.page,
          totalDePaginas
        })
      }
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('registroBancoDeHorasEmpregado', {
        acumuladoPositivo: [],
        acumuladoNegativo: [],
        indisponivelPorAgrupamento: false,
        loading: false,
        error: true,
        page: state.registroBancoDeHorasEmpregado.page,
        totalDePaginas: state.registroBancoDeHorasEmpregado.totalDePaginas
      })
    }
  },
  async verificarBancoDeHorasNovoOuAntigo ({commit}) {
    commit('registroBancoDeHorasEmpregado', {
      acumuladoPositivo: [],
      acumuladoNegativo: [],
      indisponivelPorAgrupamento: false,
      loading: true,
      error: false,
      page: 1,
      totalDePaginas: 1
    })

    try {
      const chamadaAPI = await API.whbCount.get({})

      let verificaSeEhNovoBanco = false

      if (chamadaAPI.status === 200) {
        const resultado = chamadaAPI.body.success
        verificaSeEhNovoBanco = Boolean(resultado.new !== 0)
      }

      return verificaSeEhNovoBanco
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('registroBancoDeHorasEmpregado', {indisponivelPorAgrupamento: false, loading: false, error: true})
      return 'erro'
    }
  },
  // Widget "Atraso da equipe" — foto do dia (tempo real). Reaproveita o
  // endpoint acompanhamento-diario/geral e a matemática dos 4 status do painel
  // de Acompanhamento em Tempo Real. Ignora a competência: dia = hoje.
  async carregarDadosAtraso ({ commit, state, rootState }, parametros) {
    commit('atrasoEquipe', { ...state.atrasoEquipe, loading: true, error: false })
    try {
      const filtro = comporFiltroAtraso({
        ...parametros,
        hasAcessoPorLideranca: rootState.userInfo.compMan.hasAcessoPorLideranca
      })
      const data = await obterFonteDeDadosAcompanhamentoEmTempoReal().buscarAcompanhamento(filtro)
      if (!data.estados || !data.eventos) {
        throw new Error('Resposta inválida do acompanhamento em tempo real')
      }
      const estados = converterChavesDeObjeto(data.estados || {})
      const eventos = converterChavesDeObjeto(data.eventos || {})

      // Matemática dos 4 status (espelha os getters do painel Tempo Real).
      const emAtraso = estados.emAtraso?.total || 0
      const aIniciar = (estados.aEntrarEmBreve?.total || 0) + (estados.antesDeTrabalhar?.total || 0)
      const trabalhando = chavesReferentesAosEstadoTrabalhando.reduce(
        (soma, chave) => soma + (estados[chave]?.total || 0), 0
      )
      const ausencias = Object.values(eventos.ausencias || {})
      const ausentes = ausencias.reduce((soma, item) => soma + (item.total || 0), 0) + (estados.faltou?.total || 0)
      const possuiDetalhesPorStatus = {
        emAtraso: Array.isArray(estados.emAtraso?.funcionarios),
        aIniciar: [estados.aEntrarEmBreve, estados.antesDeTrabalhar]
          .some(estado => Array.isArray(estado?.funcionarios)),
        trabalhando: chavesReferentesAosEstadoTrabalhando
          .some(chave => Array.isArray(estados[chave]?.funcionarios)),
        ausentes: [...ausencias, estados.faltou]
          .some(estado => Array.isArray(estado?.funcionarios))
      }

      const funcionariosPorStatus = normalizarFuncionariosPorStatus({
        emAtraso: estados.emAtraso?.funcionarios || [],
        aIniciar: [
          ...(estados.aEntrarEmBreve?.funcionarios || []),
          ...(estados.antesDeTrabalhar?.funcionarios || [])
        ],
        trabalhando: chavesReferentesAosEstadoTrabalhando.flatMap(chave => estados[chave]?.funcionarios || []),
        // Inclui faltou na lista para casar com a contagem (o spec soma ambos).
        ausentes: [
          ...ausencias.flatMap(item => item.funcionarios || []),
          ...(estados.faltou?.funcionarios || [])
        ]
      })

      commit('atrasoEquipe', {
        loading: false,
        error: false,
        indisponivelPorAgrupamento: false,
        total: data.total_de_empregados || data.totalDeEmpregados || 0,
        contagens: {
          emAtraso: possuiDetalhesPorStatus.emAtraso ? funcionariosPorStatus.emAtraso.length : emAtraso,
          aIniciar: possuiDetalhesPorStatus.aIniciar ? funcionariosPorStatus.aIniciar.length : aIniciar,
          trabalhando: possuiDetalhesPorStatus.trabalhando ? funcionariosPorStatus.trabalhando.length : trabalhando,
          ausentes: possuiDetalhesPorStatus.ausentes ? funcionariosPorStatus.ausentes.length : ausentes
        },
        funcionariosPorStatus,
        horarioUltimaAtualizacao: moment(new Date()).format('HH:mm:ss')
      })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('atrasoEquipe', { ...state.atrasoEquipe, loading: false, error: true })
    }
  },
  // Widget "Férias do time": agendadas + solicitações PENDENTE (hoje → +180d)
  // e saldos a vencer em 180 dias, para as entidades do agrupamento
  // selecionado. gestao-de-ferias aceita grupo/local/empregado nativamente,
  // então não há estado "indisponível por agrupamento" aqui.
  async carregarFeriasDoTime ({ commit, state, dispatch }, parametros) {
    let entidades = montarEntidadesDoAgrupamento(parametros)
    if (!entidades && parametros.tipo === 'liderDireto') {
      const empregados = await dispatch('resolverEmpregadosDoEscopo', parametros.usuarioLider)
      if (empregados.length) entidades = { tipo: 'empregado', ids: empregados.map(empregado => empregado.id) }
    }
    if (!entidades) {
      commit('feriasDoTime', {
        loading: false, error: false, proximas: [], vencimentos: [], totalProximas: 0, totalVencimentos: 0
      })
      return
    }

    commit('feriasDoTime', { ...state.feriasDoTime, loading: true, error: false })
    try {
      const repositorio = await obterRepositorioDeFerias()
      const dados = await buscarDadosDeFerias(repositorio, entidades, new Date())
      commit('feriasDoTime', { loading: false, error: false, ...dados })
    } catch (erro) {
      FiltradorDeErros.capturarErro(erro)
      commit('feriasDoTime', { ...state.feriasDoTime, loading: false, error: true })
    }
  },
  async carregarTodasEstatisticasHome ({dispatch, commit}, payload) {
    const {
      parametros,
      carregarAcompanhamento = false,
      carregarFerias = false
    } = payload
    commit('filtroDeAgrupamentoAtual', parametros)
    const carregamentos = [
      dispatch('carregarDadosTravamentosPendentes', parametros),
      dispatch('carregarDadosJornadasComProblemas', parametros),
      dispatch('verificarECarregarBancoDeHoras', parametros),
      dispatch('carregarEventosExcepcionais', parametros),
      dispatch('carregarEventosProgramados', parametros),
      dispatch('carregarDadosDeAprovacao', parametros)
    ]

    if (carregarAcompanhamento) carregamentos.push(dispatch('carregarDadosAtraso', parametros))
    if (carregarFerias) carregamentos.push(dispatch('carregarFeriasDoTime', parametros))

    return Promise.allSettled(carregamentos)
  }
}

export default {
  namespaced: true,
  state: defaultState(),
  getters,
  mutations,
  actions
}
